import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useHref, useNavigate } from "react-router-dom";
import { FEATURED_PROMOTION } from "../../../constants/messages/promotions.js";
import useQueryParams from "../../../hooks/useQueryParams.js";
import { useGetOfferQuery, useGetVRMDataQuery } from "../api/offerApi.js";
import { getError } from "../slices/offerSlice.js";

const useOffer = () => {
  const navigate = useNavigate();
  const href = useHref("/");

  // Get basename from the BrowserRouter to determine Owner state
  const isOwner = useMemo(() => href.startsWith("/owners"), [href]);

  const { quoteNumber, coId, companionId } = useQueryParams();

  const error = useSelector(getError);

  const { data, isLoading, isUninitialized } = useGetOfferQuery(undefined, {
    skip: !quoteNumber || !coId,
  });

  const { data: vrmData } = useGetVRMDataQuery(undefined, {
    skip: !!quoteNumber || !coId,
  });

  const {
    ReservationID,
    QuoteNumber,
    COID,
    FirstName,
    LastName,
    Email,
    Phone,
    CheckinDate,
    CheckoutDate,
    Property = {},
    CreditType,
    CreditMode,
    CreditAmount,
    CreditThreshold,
    ExtraCreditAmount,
    DeliveryFeeThreshold,
    GuaranteedDeliveryAvailable,
    Companions,
    AvailableCoupons,
    Modification: modificationData,
  } = data || {};

  const isCompanion = useMemo(() => {
    if (companionId && Companions.length > 0) {
      const companion = Companions.find(
        ({ CompanionID }) => String(CompanionID) === companionId,
      );

      return !!companion;
    }

    return false;
  }, [Companions, companionId]);

  const FeaturedPromotion = useMemo(() => {
    if (!modificationData && AvailableCoupons?.length) {
      const { Code, Effects } = AvailableCoupons[0];

      if (Code && Effects) {
        const [Key, Discount] = Object.entries(Effects)[0];
        const Name = FEATURED_PROMOTION[Key]?.NAME ?? Code;
        const Tagline = `${Discount}% Off ${Name}`;

        return { Code, Discount, Key, Name, Tagline };
      }

      return null;
    }
  }, [modificationData, AvailableCoupons]);

  const VRMData = useMemo(() => {
    const { slug, logo, name } = vrmData?.data || {};
    return {
      COID: COID || slug,
      VRMLogo: Property?.Logo || logo,
      VRMName: name ?? "",
    };
  }, [vrmData, COID, Property]);

  const Reservation = useMemo(() => {
    if (!quoteNumber) return null;

    return {
      ReservationID,
      QuoteNumber,
      COID,
      CheckinDate,
      CheckoutDate,
      Companions,
      ...(modificationData
        ? {
            FirstName: modificationData.FirstName,
            LastName: modificationData.LastName,
            Email: modificationData.Email,
            Phone: modificationData.Phone,
          }
        : { FirstName, LastName, Email, Phone }),
    };
  }, [modificationData]);

  const Modification = useMemo(() => {
    if (!modificationData) return null;

    const {
      PromotionTotalAmountApplied: PromotionTotal,
      PreexistingFee: PreexistingFeeTotal,
      DeliveryFee: DeliveryFeeStandardTotal,
      DeliveryFeeGuaranteed: DeliveryFeeGuaranteedTotal,
      CreditTotalAmountApplied: DiscountTotal,
      LateFee: LateFeeTotal,
      DamageFee: GearDamage,
      ...rest
    } = modificationData;

    return {
      ...rest,
      PromotionTotal,
      PreexistingFeeTotal,
      DeliveryFeeStandardTotal,
      DeliveryFeeGuaranteedTotal,
      DiscountTotal,
      LateFeeTotal,
      GearDamage,
    };
  }, [modificationData]);

  // Handle offer errors
  useEffect(() => {
    if (error) {
      navigate("/error", { replace: true });
    }
  }, [error]);

  return {
    data,
    isLoading,
    offerLoaded: !isUninitialized,
    error,
    isOwner,
    isCompanion,
    isModification: !!Modification,
    FeaturedPromotion,
    Modification,
    Reservation,
    VRMData,
    CreditDiscount: {
      CreditType,
      CreditMode,
      CreditAmount,
      CreditThreshold,
      ExtraCreditAmount,
    },
    DeliveryFeeThreshold,
    Property,
    GuaranteedDeliveryAvailable,
  };
};

export default useOffer;
